import { Box, Button, Typography } from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { sortJudgesForDisplay } from "score-common";
import { CLASS_STATUS_KIND } from "../utils/lookupColumn";

// #3393 (Announcer Live Desk redesign, part 2) -- the right column's fixed
// heading: the class being viewed. Purely presentational (LookupColumn owns
// the data); it never scrolls, the ride list below it does.
//   viewing the live class -> green LIVE tag (static, no pulse) + title
//   viewing any other class -> outlined "‹ Back" button (+ trophy if placed)
//                              + title
// then the judges line, then the status line. Green means live only.

const STATUS_TESTID = {
  [CLASS_STATUS_KIND.PLACED]: "placed",
  [CLASS_STATUS_KIND.IN_PROGRESS]: "in-progress",
  [CLASS_STATUS_KIND.NOT_STARTED]: "not-started",
};

export default function LookupHeading({
  following,
  title,
  placed,
  statusKind,
  judges,
  backLabel,
  returnDot,
  onBack,
}) {
  const { t } = useTranslation("announcer");

  // "B: Landry Marchetti, C: Quinn Carrington" in arena order; a judge whose
  // name never resolved renders the position alone, no dangling "M: " (#2644).
  const judgesText = sortJudgesForDisplay(judges || [], (j) => j.position)
    .map((j) =>
      j.name ? t("results.judgeAtPosition", { position: j.position, name: j.name }) : j.position || "",
    )
    .filter(Boolean)
    .join(", ");

  const statusText = {
    [CLASS_STATUS_KIND.PLACED]: t("lookupColumn.statusPlaced"),
    [CLASS_STATUS_KIND.IN_PROGRESS]: t("lookupColumn.statusInProgress"),
    [CLASS_STATUS_KIND.NOT_STARTED]: t("lookupColumn.statusNotStarted"),
  }[statusKind];
  // Orange = placed; green = live only (an in-progress class that is NOT the
  // live one is not "live", so it stays neutral).
  const statusColor =
    statusKind === CLASS_STATUS_KIND.PLACED
      ? colors.foxOrange
      : statusKind === CLASS_STATUS_KIND.IN_PROGRESS && following
        ? "success.main"
        : "text.secondary";

  return (
    <Box
      data-testid="lookup-column-header"
      data-following={following ? "true" : "false"}
      sx={{
        flexShrink: 0,
        boxSizing: "border-box",
        bgcolor: "background.paper",
        // right gutter keeps the title clear of the overlay's close button
        pl: 1.5,
        pr: 5,
        py: 1,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
        {following ? (
          <Typography
            data-testid="lookup-live-tag"
            component="span"
            sx={{
              flexShrink: 0,
              px: 0.75,
              py: 0.125,
              borderRadius: 0.5,
              bgcolor: "success.main",
              color: "success.contrastText",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.5,
              lineHeight: 1.4,
            }}
          >
            {t("lookupColumn.live")}
          </Typography>
        ) : (
          <>
            <Button
              data-testid="lookup-return-button"
              variant="outlined"
              size="small"
              aria-label={backLabel}
              title={backLabel}
              onClick={onBack}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontSize: 12,
                lineHeight: 1.3,
                py: 0.25,
                px: 1,
                minWidth: 0,
                color: colors.foxOrange,
                borderColor: colors.foxOrange,
                "&:hover": { borderColor: colors.foxOrange, bgcolor: "rgba(248, 153, 29, 0.08)" },
              }}
            >
              {t("lookupColumn.back")}
            </Button>
            {returnDot && (
              <Box
                data-testid="lookup-return-dot"
                sx={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", bgcolor: colors.foxOrange }}
              />
            )}
            {placed && (
              <EmojiEventsIcon
                data-testid="lookup-heading-trophy"
                fontSize="small"
                sx={{ flexShrink: 0, color: colors.foxOrange }}
              />
            )}
          </>
        )}
        {title ? (
          // Never wraps: ellipsis, full name in the tooltip.
          <Typography
            data-testid="lookup-class-heading"
            title={title}
            noWrap
            variant="subtitle1"
            sx={{ fontWeight: 700, minWidth: 0, flex: 1 }}
          >
            {title}
          </Typography>
        ) : null}
      </Box>

      {judgesText ? (
        <Typography data-testid="lookup-judges" variant="body2" color="text.secondary" noWrap title={judgesText}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            {t("results.judgesLabel")}
          </Box>{" "}
          {judgesText}
        </Typography>
      ) : null}

      <Typography
        data-testid="lookup-status-line"
        data-status={STATUS_TESTID[statusKind]}
        variant="caption"
        noWrap
        sx={{ display: "block", fontWeight: 700, letterSpacing: 0.5, color: statusColor }}
      >
        {statusText}
      </Typography>
    </Box>
  );
}
